// The popup that opens when a producer clicks a crew member on the live
// map.
//
// Built as a string because mapbox-gl popups take HTML, not React. Kept
// out of LiveTracking.tsx so the content is testable on its own — the
// component around it needs a live mapbox instance to exist at all.
//
// Two deliberate changes from what this used to show:
//   * "Now" carries its set time, same as "Next up" always did. The set
//     time of the artist currently being filmed is the thing a producer
//     actually needs mid-show, and it was the one that was missing.
//   * The GPS diagnostics block is gone (source, confidence, correction
//     magnitude, live/stale). It described how the marker was placed,
//     not where the crew member is or what they're doing.

import { CREW_MARKER_COLORS } from "@produce/shared";
import { escapeHtml } from "@/lib/escapeHtml";

/** One assignment as the popup shows it. Times are already "HH:MM". */
export interface CrewPopupAssignment {
  /** Artist name, else the custom subject, else the stage. */
  title: string;
  stageName: string;
  filmingStart: string;
  filmingEnd: string;
  /** The artist's on-stage window, when the lineup slot is known. */
  setStart?: string | null;
  setEnd?: string | null;
  notes?: string | null;
}

export interface CrewPopupData {
  name: string;
  functionLabel: string;
  /** e.g. "Last seen 2 minutes ago". */
  lastSeenLabel: string;
  current: CrewPopupAssignment | null;
  next: CrewPopupAssignment | null;
  /** e.g. "42 min left"; only meaningful alongside `current`. */
  timeLeftLabel?: string | null;
}

const COLORS = {
  surface: "hsl(220, 25%, 12%)",
  divider: "hsl(220, 20%, 18%)",
  heading: "hsl(0, 0%, 100%)",
  muted: "hsl(0, 0%, 60%)",
  // Same lime as the ring this popup describes, so the two cannot drift.
  accent: CREW_MARKER_COLORS.progress,
} as const;

const ACCENT_WASH = "rgba(209, 255, 56, 0.16)";
const MUTED_WASH = "rgba(147, 164, 187, 0.14)";

const tag = (label: string, color: string, background: string): string => `
  <span style="display:inline-block;font-size:10px;font-weight:800;letter-spacing:0.1em;padding:2px 7px;border-radius:4px;background:${background};color:${color};margin-bottom:6px;">
    ${escapeHtml(label)}
  </span>`;

const assignmentBlock = (
  assignment: CrewPopupAssignment,
  slot: "now" | "next",
  timeLeftLabel?: string | null,
): string => {
  const isCurrent = slot === "now";
  const setTime =
    assignment.setStart && assignment.setEnd
      ? `<div style="font-size:12px;color:${COLORS.muted};margin-top:2px;">Set time ${escapeHtml(
          assignment.setStart,
        )} – ${escapeHtml(assignment.setEnd)}</div>`
      : "";

  const remaining =
    isCurrent && timeLeftLabel
      ? ` · <span style="color:${COLORS.accent};font-weight:600;">${escapeHtml(timeLeftLabel)}</span>`
      : "";

  return `
    <div style="border-top:1px solid ${COLORS.divider};padding-top:10px;margin-top:10px;">
      ${tag(
        isCurrent ? "NOW" : "NEXT UP",
        isCurrent ? COLORS.accent : COLORS.muted,
        isCurrent ? ACCENT_WASH : MUTED_WASH,
      )}
      <div style="font-size:15px;font-weight:700;color:${COLORS.heading};">${escapeHtml(assignment.title)}</div>
      <div style="font-size:12px;color:${COLORS.muted};margin-top:2px;">
        ${escapeHtml(assignment.filmingStart)} – ${escapeHtml(assignment.filmingEnd)} · ${escapeHtml(assignment.stageName)}${remaining}
      </div>
      ${setTime}
    </div>`;
};

/** Render the popup body. Every interpolated value is escaped. */
export function buildCrewMarkerPopupHtml(data: CrewPopupData): string {
  const blocks: string[] = [];

  if (data.current) {
    blocks.push(assignmentBlock(data.current, "now", data.timeLeftLabel));
  } else {
    blocks.push(`
      <div style="border-top:1px solid ${COLORS.divider};padding-top:10px;margin-top:10px;">
        ${tag("NOW", COLORS.muted, MUTED_WASH)}
        <div style="font-size:13px;color:${COLORS.muted};">No assignment right now</div>
      </div>`);
  }

  if (data.next) {
    blocks.push(assignmentBlock(data.next, "next"));
  }

  const notes = data.current?.notes || data.next?.notes;
  const notesBlock = notes
    ? `<div style="border-top:1px solid ${COLORS.divider};padding-top:10px;margin-top:10px;font-size:12px;color:${COLORS.muted};">${escapeHtml(notes)}</div>`
    : "";

  return `
    <div style="min-width:260px;padding:14px 16px 15px;background:${COLORS.surface};border-radius:12px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
      <div>
        <div style="font-size:16px;font-weight:700;color:${COLORS.heading};">${escapeHtml(data.name)}</div>
        <div style="font-size:12px;color:${COLORS.muted};">${escapeHtml(data.functionLabel)}</div>
      </div>
      ${blocks.join("")}
      ${notesBlock}
      <div style="border-top:1px solid ${COLORS.divider};padding-top:10px;margin-top:10px;font-size:11.5px;color:${COLORS.muted};">
        ${escapeHtml(data.lastSeenLabel)}
      </div>
    </div>`;
}
